import type { ReactNode } from 'react';
import { Form, Lines, PageHeading, Rows, SkeletonBlock } from './SkeletonPrimitives';
import { PostSkeleton } from './CommunitySkeletons';
import '../pages/followers.css';
import './profilePosts.css';
import './global-search.css';
import './demoMembers.css';
import './profileSkeletons.css';

function Tabs({ count = 3 }: { count?: number }) {
  return (
    <div className="sk-profile-tabs">
      {Array.from({ length: count }, (_, index) => (
        <SkeletonBlock className="sk-profile-tab" key={index} />
      ))}
    </div>
  );
}

function Gallery({
  owner = false,
  sampleGallery = false,
}: {
  owner?: boolean;
  sampleGallery?: boolean;
}) {
  const count = sampleGallery || !owner ? 5 : 8;
  return (
    <div className="gym-gallery sk-profile-gallery">
      <div className="gym-gallery-head">
        <SkeletonBlock className="sk-title sk-profile-section-title" />
      </div>
      <div className={owner ? 'gym-gallery-toolbar' : undefined}>
        {owner && <SkeletonBlock className="sk-line sk-profile-gallery-hint" />}
        <div className={owner ? 'gym-gallery-toolbar-stage' : undefined}>
          <div className="gym-gallery-carousel">
            <div className="gym-gallery-track" aria-hidden="true">
              {Array.from({ length: count }, (_, index) => (
                <SkeletonBlock className="sk-profile-gallery-photo gym-gallery-tile" key={index} />
              ))}
            </div>
          </div>
        </div>
        {owner && (
          <div className="gym-gallery-actions">
            <SkeletonBlock className="sk-control" />
            <SkeletonBlock className="sk-control" />
            <SkeletonBlock className="sk-control" />
          </div>
        )}
      </div>
    </div>
  );
}

function Comments({ count }: { count: number }) {
  return (
    <div className="gym-comment-list">
      {Array.from({ length: count }, (_, index) => (
        <div className="gym-comment" key={index}>
          <SkeletonBlock className="sk-profile-comment-avatar" />
          <div className="sk-profile-comment-copy">
            <SkeletonBlock className="sk-line sk-profile-comment-name" />
            <Lines />
            <SkeletonBlock className="sk-line sk-profile-comment-actions" />
          </div>
        </div>
      ))}
    </div>
  );
}

function Posts({ count }: { count: number }) {
  return (
    <div className="profile-posts-list">
      <PostSkeleton count={count} />
    </div>
  );
}

function Equipment({ count }: { count: number }) {
  return (
    <div className="product-grid">
      {Array.from({ length: count }, (_, index) => (
        <div className="product-card" key={index}>
          <div className="product-image">
            <SkeletonBlock className="sk-profile-equipment-photo" />
          </div>
          <div className="product-card-body sk-profile-equipment-copy">
            <SkeletonBlock className="sk-line sk-line-short" />
            <SkeletonBlock className="sk-title" />
          </div>
        </div>
      ))}
    </div>
  );
}

function ProfileIdentity() {
  return (
    <>
      <div className="profile-summary">
        <SkeletonBlock className="member-avatar sk-profile-identity-avatar" />
        <div className="sk-profile-identity-copy">
          <SkeletonBlock className="sk-profile-identity-name" />
          <SkeletonBlock className="sk-line sk-profile-identity-meta" />
          <SkeletonBlock className="sk-line sk-profile-identity-link" />
        </div>
        <div className="profile-actions">
          <SkeletonBlock className="sk-control" />
          <SkeletonBlock className="sk-control" />
        </div>
      </div>
      <div className="profile-about">
        <Lines />
      </div>
    </>
  );
}

function ProfilePostsBlock({ count }: { count: number }) {
  return (
    <div className="profile-posts">
      <div className="profile-posts-heading">
        <SkeletonBlock className="sk-control sk-profile-posts-filter" />
        <SkeletonBlock className="sk-control sk-profile-posts-sort" />
      </div>
      <Posts count={Math.min(count, 2)} />
    </div>
  );
}

function Profile({
  owner,
  count,
  sampleGallery,
}: {
  owner: boolean;
  count: number;
  sampleGallery?: boolean;
}) {
  return (
    <div className="sk-profile-page">
      <div className="gym-hero sk-profile-hero">
        <SkeletonBlock className="sk-profile-hero-fill" />
        <div className="gym-hero-overlay">
          {owner && (
            <div className="gym-hero-actions">
              <div className="gym-hero-actions-desktop">
                <SkeletonBlock className="sk-control" />
                <SkeletonBlock className="sk-control" />
                <SkeletonBlock className="sk-control" />
              </div>
              <div className="gym-hero-actions-menu">
                <SkeletonBlock className="sk-control" />
              </div>
            </div>
          )}
          <SkeletonBlock className="sk-line sk-profile-hero-kicker" />
          <SkeletonBlock className="sk-profile-hero-name" />
        </div>
      </div>
      {!owner && <ProfileIdentity />}
      <div className="profile-gym" data-gym-tab="tour">
        <div className="section-row profile-primary-tabs">
          <Tabs />
        </div>
        <div className="profile-gym-main">
          <Gallery owner={owner} sampleGallery={sampleGallery} />
        </div>
        <div className="gym-comments">
          <div className="gym-comments-head">
            <SkeletonBlock className="sk-title sk-profile-section-title" />
          </div>
          <Comments count={Math.min(count, 3)} />
        </div>
      </div>
      {!owner && <ProfilePostsBlock count={count} />}
    </div>
  );
}

function MyProfile({ count }: { count: number }) {
  return (
    <div className="sk-profile-page">
      <ProfileIdentity />
      <ProfilePostsBlock count={count} />
    </div>
  );
}

function Followers({ count, page }: { count: number; page: boolean }) {
  return (
    <div className="relationships-page">
      {page && <PageHeading />}
      <div className="relationships-tabs">
        <Tabs />
      </div>
      <div className="relationships-search sk-profile-relationship-search">
        <SkeletonBlock className="sk-line" />
      </div>
      <div className="relationships-list">
        {Array.from({ length: count }, (_, index) => (
          <div className="relationships-person" key={index}>
            <div className="relationships-identity">
              <SkeletonBlock className="sk-profile-relationship-avatar" />
              <div className="relationships-person-copy sk-profile-relationship-copy">
                <SkeletonBlock className="sk-line" />
                <SkeletonBlock className="sk-line sk-line-short" />
              </div>
            </div>
            <div className="relationships-actions">
              <SkeletonBlock className="sk-control" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SettingsProfile() {
  return (
    <div className="stack-form">
      <SkeletonBlock className="sk-title sk-profile-section-title" />
      <div className="avatar-settings">
        <SkeletonBlock className="sk-profile-settings-avatar" />
        <div className="sk-profile-settings-avatar-copy">
          <Lines short />
          <div className="sk-profile-inline-controls">
            <SkeletonBlock className="sk-control" />
            <SkeletonBlock className="sk-control" />
          </div>
        </div>
      </div>
      <Form count={4} />
      <div className="sk-field">
        <SkeletonBlock className="sk-line sk-line-short" />
        <SkeletonBlock className="sk-profile-textarea" />
      </div>
      {Array.from({ length: 3 }, (_, index) => (
        <div className="sk-field" key={index}>
          <SkeletonBlock className="sk-line sk-line-short" />
          <SkeletonBlock className="sk-control sk-profile-full-control" />
        </div>
      ))}
      <SkeletonBlock className="sk-control" />
    </div>
  );
}

function SettingsSection({ children }: { children: ReactNode }) {
  return (
    <div className="settings-section">
      <SkeletonBlock className="sk-profile-subsection-title" />
      {children}
    </div>
  );
}

function Description() {
  return (
    <div className="sk-profile-section-description">
      <Lines />
    </div>
  );
}

function SettingsLinks() {
  return (
    <div className="sk-profile-settings-links">
      <div className="section-row">
        <div>
          <SkeletonBlock className="sk-title sk-profile-section-title" />
          <Description />
        </div>
        <SkeletonBlock className="sk-control" />
      </div>
      {Array.from({ length: 3 }, (_, index) => (
        <div className="social-setting-row" key={index}>
          <SkeletonBlock className="sk-profile-social-icon" />
          <div className="sk-profile-social-copy">
            <Lines short />
            <SkeletonBlock className="sk-line sk-line-medium" />
          </div>
          <SkeletonBlock className="sk-profile-social-icon" />
          <SkeletonBlock className="sk-profile-social-icon" />
          <SkeletonBlock className="sk-control sk-profile-small-action" />
        </div>
      ))}
    </div>
  );
}

function SettingsNotifications() {
  return (
    <div className="sk-profile-settings-preferences">
      <SkeletonBlock className="sk-title sk-profile-section-title" />
      <Description />
      {Array.from({ length: 7 }, (_, index) => (
        <div className="preference-row" key={index}>
          <div className="sk-profile-notification-copy">
            <SkeletonBlock className="sk-profile-notification-label" />
            {index === 6 && <Description />}
          </div>
          <SkeletonBlock className="sk-profile-toggle" />
        </div>
      ))}
      <SkeletonBlock className="sk-line sk-profile-settings-hint" />
    </div>
  );
}

function SettingsSecurity() {
  return (
    <div className="sk-profile-settings-security">
      <SkeletonBlock className="sk-title sk-profile-section-title" />
      <Description />
      <SettingsSection>
        <div className="stack-form">
          <Description />
          <SkeletonBlock className="sk-control sk-profile-full-control" />
          <SkeletonBlock className="sk-control sk-profile-wide-action" />
        </div>
      </SettingsSection>
      <SettingsSection>
        <Description />
        <SkeletonBlock className="sk-control" />
      </SettingsSection>
      <SettingsSection>
        <div className="stack-form">
          <Description />
          <SkeletonBlock className="sk-control sk-profile-full-control" />
          <SkeletonBlock className="sk-control sk-profile-wide-action" />
        </div>
      </SettingsSection>
      <SettingsSection>
        <div className="stack-form">
          <SkeletonBlock className="sk-control sk-profile-full-control" />
          <Description />
          <SkeletonBlock className="sk-control sk-profile-wide-action" />
          <div className="sk-field">
            <SkeletonBlock className="sk-line sk-line-short" />
            <SkeletonBlock className="sk-control sk-profile-full-control" />
          </div>
          <SkeletonBlock className="sk-control" />
        </div>
      </SettingsSection>
      <SettingsSection>
        <Description />
        <div className="sk-profile-inline-controls">
          <SkeletonBlock className="sk-control sk-profile-wide-action" />
          <SkeletonBlock className="sk-control sk-profile-wide-action" />
        </div>
      </SettingsSection>
    </div>
  );
}

function SettingsPrivacy() {
  return (
    <div className="sk-profile-settings-privacy">
      <SkeletonBlock className="sk-title sk-profile-section-title" />
      <Description />
      <SettingsSection>
        <Description />
        <SkeletonBlock className="sk-profile-subsection-title" />
        <Description />
      </SettingsSection>
      <SettingsSection>
        <Description />
        <SkeletonBlock className="sk-control" />
      </SettingsSection>
      <SettingsSection>
        <Description />
        <SkeletonBlock className="sk-control" />
      </SettingsSection>
      <SettingsSection>
        <div className="section-row">
          <SkeletonBlock className="sk-line sk-profile-status-label" />
          <SkeletonBlock className="sk-control sk-profile-small-action" />
        </div>
        <Preferences count={3} />
      </SettingsSection>
      <SettingsSection>
        <Description />
        <SkeletonBlock className="sk-control sk-profile-wide-action" />
      </SettingsSection>
    </div>
  );
}

function Settings({ page, search }: { page: boolean; search: string }) {
  const requested = page ? new URLSearchParams(search).get('tab') : null;
  const content =
    requested === 'links' ? (
      <SettingsLinks />
    ) : requested === 'notifications' ? (
      <SettingsNotifications />
    ) : requested === 'security' ? (
      <SettingsSecurity />
    ) : requested === 'privacy' ? (
      <SettingsPrivacy />
    ) : (
      <SettingsProfile />
    );
  return (
    <>
      {page && <PageHeading />}
      <div className="settings-layout">
        <div className="settings-nav sk-profile-settings-nav">
          {Array.from({ length: 5 }, (_, index) => (
            <SkeletonBlock className="sk-control" key={index} />
          ))}
        </div>
        <div className="panel settings-panel">{content}</div>
      </div>
    </>
  );
}

function StatusRows({ count }: { count: number }) {
  return (
    <div className="sk-profile-status-rows">
      {Array.from({ length: count }, (_, index) => (
        <div className="status-row" key={index}>
          <SkeletonBlock className="sk-line sk-profile-status-label" />
          <SkeletonBlock className="sk-line sk-profile-status-value" />
        </div>
      ))}
    </div>
  );
}

function AdminMembers({ count }: { count: number }) {
  return (
    <div>
      {Array.from({ length: count }, (_, index) => (
        <div className="member-admin-row" key={index}>
          <SkeletonBlock className="sk-avatar" />
          <div className="sk-profile-admin-member-copy">
            <Lines short />
          </div>
          <SkeletonBlock className="sk-control sk-profile-small-action" />
          <SkeletonBlock className="sk-control sk-profile-small-action" />
          <SkeletonBlock className="sk-control sk-profile-role-select" />
        </div>
      ))}
    </div>
  );
}

function Preferences({ count }: { count: number }) {
  return (
    <div>
      {Array.from({ length: count }, (_, index) => (
        <div className="preference-row" key={index}>
          <div className="sk-profile-preference-copy">
            <Lines />
          </div>
          <SkeletonBlock className="sk-control sk-profile-small-action" />
        </div>
      ))}
    </div>
  );
}

function Records({ count }: { count: number }) {
  return (
    <div className="records-list sk-profile-records">
      {Array.from({ length: count }, (_, index) => (
        <div className="sk-profile-record" key={index}>
          <SkeletonBlock className="sk-line sk-profile-record-name" />
          <SkeletonBlock className="sk-line sk-profile-record-id" />
          <SkeletonBlock className="sk-line sk-profile-record-action" />
        </div>
      ))}
    </div>
  );
}

function Admin({ page, search }: { page: boolean; search: string }) {
  const records = page && new URLSearchParams(search).get('tab') === 'records';
  return (
    <>
      {page && <PageHeading action />}
      <div className="admin-tabs">
        <Tabs count={6} />
      </div>
      {records ? (
        <div className="panel admin-section sk-profile-admin-records-page">
          <div className="section-row">
            <div className="sk-profile-admin-records-heading">
              <SkeletonBlock className="sk-title sk-profile-section-title" />
              <Description />
            </div>
            <SkeletonBlock className="sk-control sk-profile-wide-action" />
          </div>
          <div className="stack-form">
            <SkeletonBlock className="sk-line sk-line-short" />
            <SkeletonBlock className="sk-control sk-profile-full-control" />
          </div>
          <Records count={6} />
        </div>
      ) : (
        <>
          <div className="admin-stats">
            {Array.from({ length: 4 }, (_, index) => (
              <div className="panel" key={index}>
                <SkeletonBlock className="sk-profile-stat-icon" />
                <SkeletonBlock className="sk-profile-stat-value" />
                <SkeletonBlock className="sk-line sk-profile-stat-label" />
              </div>
            ))}
          </div>
          <div className="detail-columns">
            <div className="panel admin-section">
              <SkeletonBlock className="sk-title sk-profile-section-title" />
              <StatusRows count={8} />
            </div>
            <div className="panel admin-section sk-profile-responsibilities">
              <SkeletonBlock className="sk-title sk-profile-section-title" />
              <Lines />
              <Lines />
              <Lines />
            </div>
          </div>
        </>
      )}
    </>
  );
}

function Search({ count }: { count: number }) {
  return (
    <div className="global-search-groups">
      {Array.from({ length: 4 }, (_, index) => (
        <section key={index}>
          <div className="global-search-group-heading">
            <SkeletonBlock className="sk-line sk-profile-search-heading" />
            <SkeletonBlock className="sk-line sk-profile-search-more" />
          </div>
          {Array.from({ length: Math.min(count, 3) }, (_, row) => (
            <div className="global-search-result" key={row}>
              <div className="sk-profile-search-copy">
                <SkeletonBlock className="sk-line" />
                <SkeletonBlock className="sk-line sk-line-medium" />
              </div>
              <SkeletonBlock className="sk-profile-search-arrow" />
            </div>
          ))}
        </section>
      ))}
    </div>
  );
}

/** Matches the search dialog's query/hint scaffolding before its chunk is ready. */
export function GlobalSearchScaffold({ children }: { children: ReactNode }) {
  return (
    <div className="global-search">
      <SkeletonBlock className="sk-profile-search-input" />
      <SkeletonBlock className="sk-profile-search-hint" />
      {children}
    </div>
  );
}

export function ProfileSkeleton({
  variant,
  count,
  page = false,
  sampleGallery,
  search = '',
}: {
  variant: string;
  count: number;
  page?: boolean;
  sampleGallery?: boolean;
  search?: string;
}) {
  switch (variant) {
    case 'profile':
    case 'gym':
      return <Profile owner={variant === 'gym'} count={count} sampleGallery={sampleGallery} />;
    case 'my-profile':
      return <MyProfile count={count} />;
    case 'followers':
      return <Followers count={count} page={page} />;
    case 'settings':
      return <Settings page={page} search={search} />;
    case 'admin':
      return <Admin page={page} search={search} />;
    case 'form':
      return <Form count={count} />;
    case 'search':
      return <Search count={count} />;
    case 'profile-posts':
      return <Posts count={count} />;
    case 'profile-equipment':
      return <Equipment count={count} />;
    case 'gym-comments':
      return <Comments count={count} />;
    case 'gym-gallery':
      return <Gallery />;
    case 'admin-status':
      return <StatusRows count={count} />;
    case 'admin-members':
      return <AdminMembers count={count} />;
    case 'admin-records':
      return <Records count={count} />;
    case 'account-requests':
      return <Preferences count={count} />;
    case 'auth-status':
      return (
        <div className="sk-profile-auth-status">
          <Lines short />
        </div>
      );
    case 'security-status':
      return (
        <div className="sk-profile-security-status">
          <Lines />
          <SkeletonBlock className="sk-control" />
        </div>
      );
    default:
      return <Rows count={count} />;
  }
}
